import { useEffect, useMemo, useState } from "react";
import {
  FieldReportsFilter,
  FieldReportsFilterTypes,
  FieldReportsFilterSort,
} from "./components/Filters";
import { FieldReportsSectionCard } from "./FieldReportsSectionCard";
import Spinner from "react-spinkit";
import { endpoints } from "serverDetails";
import { FieldReportsPagination } from "./FieldReportsPagination";
import { isSameDay } from "date-fns";
import { useFetchFields } from "hooks/useFetchFieldReports";
import axios from "api";
import { getDateRange, TimeInterval } from "helpers/dates";
import { useFetchFieldDetails } from "hooks/useFetchFieldDetails";

const getReportStatuses = async (ReportIds: number[]) => {
  const payload = {
    reportIds: ReportIds,
  };

  if (ReportIds.length === 0) return [];

  try {
    const response = await axios.post(
      endpoints.reportsModule.getreportgenerationstatus,
      ReportIds
    );
    if (response && response.data && response.data.details) {
      return response.data.details.reports;
    } else {
    }
  } catch (error) {}
};

export const FieldReportsSectionPage = ({}: { title?: string }) => {
  const [reportStatuses, setReportStatuses] = useState<
    {
      reports_id: number;
      b_generate_report: boolean;
    }[]
  >([]);
  const [selectedFarm, setSelectedFarm] = useState(0);
  const [orderBy, setOrderByList] = useState<string[]>(["0"]);
  const [cropTypeFilter, setCropTypeFilter] = useState<string[]>([]);
  const [fieldTypeFilter, setFieldTypeFilter] = useState<string[]>([]);
  const [dateFilter, setDateFilter] = useState<string[]>([
    new Date().toISOString(),
  ]);
  const [dateFilterInterval, setDateFilterInterval] = useState<string>(
    "All dates"
  );
  const [sortDirection, setSortDirection] = useState<string>("Highest first");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<string>("10");
  const [filterChanged, setFilterChanged] = useState<boolean>(true);

  const addReportToRegeneratingList = (reportId: number) => {
    setReportStatuses([
      ...reportStatuses.filter(({ reports_id }) => reports_id !== reportId),
      {
        reports_id: reportId,
        b_generate_report: true,
      },
    ]);
  };

  const [farmList, cropTypeList] = useFetchFieldDetails();

  const clearFiltersAndUpdate = () => {
    clearFilters();
    setFilterChanged(prev => !prev);
  };

  const clearFilters = () => {
    setFieldTypeFilter([]);
    setCropTypeFilter([]);
    setDateFilter([new Date().toISOString()]);
    setPageNumber(0);
    setDateFilterInterval("All dates");
  };

  useEffect(() => {
    clearFiltersAndUpdate();
  }, [selectedFarm]);

  const fieldList: { name: string; id: number }[] = useMemo(() => {
    const matchingFarm =
      farmList && farmList.find(({ id }) => id === selectedFarm);
    if (matchingFarm && matchingFarm.fields) {
      return matchingFarm.fields.map(({ field_id, field_name }) => ({
        name: field_name,
        id: field_id,
      }));
    } else {
      return [];
    }
  }, [selectedFarm, farmList.length]);

  const selectedFarmName = useMemo(() => {
    if (farmList && Array.isArray(farmList)) {
      const match = farmList.find(({ id }) => id === selectedFarm);
      if (match) {
        return match.name;
      } else {
        return "";
      }
    }
  }, [selectedFarm, farmList.length]);

  const cropFilterNames = buildCropFilterNames(cropTypeFilter, cropTypeList);

  const {
    fields,
    validFieldList,
    validCropTypeList,
    validDateList,
    validSortByList,
    noResults,
    resultsCount,
    loading,
  } = useFetchFields(
    fieldTypeFilter.length > 0
      ? fieldTypeFilter.map(id => ({ fieldID: parseInt(id) }))
      : fieldList.map(({ id, name }) => ({ fieldID: id })),
    selectedFarm,
    cropFilterNames,
    parseInt(orderBy[0]),
    getDateRange(new Date(dateFilter[0]), dateFilterInterval as TimeInterval),
    sortDirection === "Lowest first" ? "ASC" : "DESC",
    pageNumber.toString(),
    pageSize,
    filterChanged,
    setDateFilter
  );

  // const { data, error } = useGetFieldReportStatus(
  //   fields.map(({ ReportsId }) => ReportsId)
  // );

  useEffect(() => {
    let intervalId: NodeJS.Timeout;
    if (fields) {
      (async () => {
        const statuses = await getReportStatuses(
          fields.map(({ ReportsId }) => ReportsId)
        );
        setReportStatuses(statuses);
      })();
      intervalId = setInterval(async () => {
        const statuses = await getReportStatuses(
          fields.map(({ ReportsId }) => ReportsId)
        );
        setReportStatuses(statuses);
      }, 60000);
    }
    return () => {
      clearInterval(intervalId);
    };
  }, [fields]);

  // if (!farmList || (Array.isArray(farmList) && farmList.length === 0)) {
  //   return (
  //     <Spinner
  //       name="three-bounce"
  //       className="BtnSpinner"
  //       fadeIn="none"
  //       style={{
  //         height: "100vh",
  //         width: "calc(100vw - 240px)",
  //         display: "flex",
  //         justifyContent: "center",
  //         alignItems: "center",
  //       }}
  //     />
  //   );
  // }

  return (
    <div className="FieldReportsSectionPage WidthContent HeightContent">
      <h1 className="FieldReportsSectionPage__title">
        {selectedFarmName ? `${selectedFarmName} Reports` : "Field Reports"}
      </h1>
      <div
        className={`FieldReportsSectionPage__Filters ${
          loading ? "Disabled" : ""
        }`}
      >
        <div className="FieldReportsSectionPage__FarmSelectWrapper">
          <FieldReportsFilterSort
            heading="Select a farm"
            items={farmList}
            selectedItem={selectedFarm}
            setSelectedItem={setSelectedFarm}
          />
          <div
            className={`Btn ${!selectedFarm || loading ? "Disabled" : ""}`}
            onClick={clearFiltersAndUpdate}
          >
            Clear filters
          </div>
        </div>
        {selectedFarm !== 0 && (
          <>
            {/* Field Filter */}
            <FieldReportsFilter
              type={FieldReportsFilterTypes.ITEMS}
              heading="Filter by Field"
              items={noResults ? [] : validFieldList}
              setSelectedItems={setFieldTypeFilter}
              selectedItems={fieldTypeFilter}
              setFilterChanged={setFilterChanged}
            />
            {/* Crop Type Filter */}
            <FieldReportsFilter
              type={FieldReportsFilterTypes.ITEMS}
              heading="Filter by Crop Type"
              items={noResults ? [] : validCropTypeList}
              setSelectedItems={setCropTypeFilter}
              selectedItems={cropTypeFilter}
              setFilterChanged={setFilterChanged}
            />
            {/* Flight Date Filter */}
            <FieldReportsFilter
              type={FieldReportsFilterTypes.DATE}
              heading="Filter by Flight Date"
              subFilterTitle="Within:"
              subFilterItems={[
                "24 hrs",
                "3 days",
                "1 week",
                "1 month",
                "1 year",
                isSameDay(new Date(dateFilter[0]), new Date())
                  ? "All dates"
                  : "All prior dates",
              ]}
              includeDates={noResults ? [] : validDateList}
              setSelectedSubFilter={setDateFilterInterval}
              selectedSubFilter={dateFilterInterval}
              setSelectedItems={setDateFilter}
              selectedItems={dateFilter}
              setFilterChanged={setFilterChanged}
            />
            {/* Order-by Filter */}
            <FieldReportsFilter
              type={FieldReportsFilterTypes.ITEMS}
              heading="Order by"
              items={validSortByList}
              selectedItems={orderBy}
              setSelectedItems={setOrderByList}
              singleSelect
              subFilterTitle="Starting with:"
              subFilterItems={["Lowest first", "Highest first"]}
              setSelectedSubFilter={setSortDirection}
              selectedSubFilter={sortDirection}
              setFilterChanged={setFilterChanged}
            />
          </>
        )}
        {noResults && !loading && (
          <div className="FieldReportsSectionPage__NoResults">No results</div>
        )}
      </div>
      {loading && (
        <Spinner
          name="three-bounce"
          className="BtnSpinner"
          fadeIn="none"
          style={{
            height: "500px",
            width: "calc(100vw - 240px)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        />
      )}
      {!loading && selectedFarm !== 0 && !noResults && fields && (
        <div className="FieldReportsSectionPage__Fields">
          {fields.map(
            ({
              CropName,
              FieldsId,
              FieldName,
              FieldReportDate,
              ReportLink,
              FieldValues,
              ThumbnailLink,
              FlightsId,
              ReportsId,
              bHasNotes,
            }) => (
              <FieldReportsSectionCard
                key={ReportsId}
                FieldId={FieldsId}
                FlightsId={FlightsId}
                FieldName={FieldName}
                FieldCrop={CropName}
                FieldReportDate={FieldReportDate}
                FieldValues={FieldValues}
                FieldReportLink={ReportLink}
                FieldImage={ThumbnailLink}
                ReportId={ReportsId}
                fieldReportStatus={reportStatuses}
                hasNotes={bHasNotes}
                addReportToRegeneratingList={addReportToRegeneratingList}
              />
            )
          )}
        </div>
      )}
      {selectedFarm !== 0 && (
        <FieldReportsPagination
          pageNumber={pageNumber}
          pageSize={parseInt(pageSize)}
          setPageNumber={number => {
            setPageNumber(number);
            setFilterChanged(prev => !prev);
          }}
          resultsCount={resultsCount}
        />
      )}
    </div>
  );
};

const buildCropFilterNames = (
  cropTypeFilter: string[],
  cropTypeList: ReturnType<typeof useFetchFieldDetails>[1]
) => {
  if (cropTypeFilter.length > 0 && cropTypeList) {
    const cropFilterIds = cropTypeFilter.map(id => parseInt(id));
    const matchingCropFilterDetails = cropTypeList.filter(({ id, name }) =>
      cropFilterIds.includes(id)
    );
    if (matchingCropFilterDetails) {
      return matchingCropFilterDetails.map(({ id, name }) => ({
        cropName: name,
      }));
    }
  }
  return cropTypeList.map(({ id, name }) => ({ cropName: name }));
};
